"use client";

import type { ErrorLike } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import {
  type PlainDate,
  type ZonedDateTime,
  getCurrentTime,
  parsePlainDate,
  toPlainDateString,
} from "@sa-nu/shared-utils/date";

import { graphql } from "../../../../../__generated__/gql";
import type {
  AccommodationFacilityType,
  AccommodationReservationProviderInput,
  StayTicketMasterReferenceInput,
} from "../../../../../__generated__/gql/graphql";
import { getDefaultReservationLeadMonths } from "../../../../../constants";
import { getSameDayMinNight } from "../../_utils/reservation-cutoff";
import type { NightAvailability } from "../../_utils/room-type-stay-availability";

const RoomTypeReservationAvailabilityDocument = graphql(`
  query RoomTypeReservationAvailability(
    $id: ID!
    $provider: AccommodationReservationProviderInput!
    $evChargerAvailable: Boolean
    $firstNight: PlainDate
    $lastNight: PlainDate
  ) {
    roomType(id: $id) {
      id
      availabilities(
        provider: $provider
        evChargerAvailable: $evChargerAvailable
        firstNight: $firstNight
        lastNight: $lastNight
      ) {
        stayTicketMasterId
        nights {
          night
          isAvailable
          price
          availableAccommodations {
            id
            facilityTypes
          }
        }
      }
    }
  }
`);

const RoomTypeReservationAvailabilityByStayTicketMasterDocument = graphql(`
  query RoomTypeReservationAvailabilityByStayTicketMaster(
    $id: ID!
    $firstNight: PlainDate!
    $lastNight: PlainDate!
    $stayTicketMasterReference: StayTicketMasterReferenceInput!
    $evChargerAvailable: Boolean
  ) {
    roomType(id: $id) {
      id
      site {
        id
        isRemote
      }
      availabilitiesByStayTicketMaster(
        firstNight: $firstNight
        lastNight: $lastNight
        stayTicketMasterReference: $stayTicketMasterReference
        evChargerAvailable: $evChargerAvailable
      ) {
        stayTicketMasterId
        nights {
          night
          isAvailable
          price
          availableAccommodations {
            id
            facilityTypes
          }
        }
      }
    }
  }
`);

/** NightAvailability に price を追加した拡張型。NightAvailability のスーパーセット。 */
export type NightAvailabilityWithPrice = NightAvailability & {
  price: number;
};

/** provider版・stayTicketMaster版どちらの availability フックも返す共通シェイプ。 */
export type RoomTypeAvailabilityResult = {
  nights: NightAvailabilityWithPrice[];
  /** accommodationId → facilityTypes。室ごと静的なため全夜を走査した last-write-wins で構築 */
  facilitiesByAccommodationId: Map<string, AccommodationFacilityType[]>;
  /** 確定金額表示可否の判定に使うStayTicketMasterのID。provider経由（availabilities）では常に null */
  stayTicketMasterId: string | null;
  loading: boolean;
  error: ErrorLike | undefined;
};

export type RoomTypeAvailabilityByStayTicketMasterResult =
  RoomTypeAvailabilityResult & {
    /** リードタイム境界日（7日/14日）の算出に使う拠点区分。取得前・roomType 不在時は null */
    isRemote: boolean | null;
  };

export function useRoomTypeAvailability({
  roomTypeId,
  provider,
  evChargerAvailable,
  currentTime = getCurrentTime(),
  skip = false,
}: {
  roomTypeId: string;
  provider: AccommodationReservationProviderInput;
  evChargerAvailable: boolean;
  currentTime?: ZonedDateTime;
  /** true のとき fetch をスキップする（日付未指定時などに使用） */
  skip?: boolean;
}): RoomTypeAvailabilityResult {
  const { data, loading, error } = useQuery(
    RoomTypeReservationAvailabilityDocument,
    {
      skip,
      variables: {
        id: roomTypeId,
        provider,
        evChargerAvailable: evChargerAvailable ? true : null,
        firstNight: toPlainDateString(getSameDayMinNight(currentTime)),
        lastNight: toPlainDateString(
          currentTime.toPlainDate().add({
            months: getDefaultReservationLeadMonths(currentTime.toPlainDate()),
          }),
        ),
      },
    },
  );

  const nights: NightAvailabilityWithPrice[] =
    data?.roomType?.availabilities.nights.map((n) => ({
      night: parsePlainDate(n.night),
      availableAccommodationIds: n.availableAccommodations.map((a) => a.id),
      price: n.price,
    })) ?? [];

  const facilitiesByAccommodationId = new Map<
    string,
    AccommodationFacilityType[]
  >(
    (data?.roomType?.availabilities.nights ?? []).flatMap((n) =>
      n.availableAccommodations.map((a) => [a.id, a.facilityTypes] as const),
    ),
  );

  return {
    nights,
    facilitiesByAccommodationId,
    stayTicketMasterId:
      data?.roomType?.availabilities.stayTicketMasterId ?? null,
    loading,
    error,
  };
}

export function useRoomTypeAvailabilityByStayTicketMaster({
  roomTypeId,
  stayTicketMasterReference,
  evChargerAvailable,
  currentTime,
  firstNight = getSameDayMinNight(currentTime),
  // lastNight は必須引数だが予約可能範囲は FE から見えないため暫定窓を渡す。
  // 空き判定はサーバー側で template の期間に再クランプされるため実害はない。
  lastNight = currentTime.toPlainDate().add({
    months: getDefaultReservationLeadMonths(currentTime.toPlainDate()),
  }),
  skip = false,
}: {
  roomTypeId: string;
  stayTicketMasterReference: StayTicketMasterReferenceInput;
  evChargerAvailable: boolean;
  currentTime: ZonedDateTime;
  /** 確定日程の範囲だけ取得したい場合に指定する */
  firstNight?: PlainDate;
  /** 確定日程の範囲だけ取得したい場合に指定する */
  lastNight?: PlainDate;
  /** true のとき fetch をスキップする */
  skip?: boolean;
}): RoomTypeAvailabilityByStayTicketMasterResult {
  const { data, loading, error } = useQuery(
    RoomTypeReservationAvailabilityByStayTicketMasterDocument,
    {
      skip,
      variables: {
        id: roomTypeId,
        firstNight: toPlainDateString(firstNight),
        lastNight: toPlainDateString(lastNight),
        stayTicketMasterReference,
        evChargerAvailable: evChargerAvailable ? true : null,
      },
    },
  );

  const nights: NightAvailabilityWithPrice[] =
    data?.roomType?.availabilitiesByStayTicketMaster.nights.map((n) => ({
      night: parsePlainDate(n.night),
      availableAccommodationIds: n.availableAccommodations.map((a) => a.id),
      price: n.price,
    })) ?? [];

  const facilitiesByAccommodationId = new Map<
    string,
    AccommodationFacilityType[]
  >(
    (data?.roomType?.availabilitiesByStayTicketMaster.nights ?? []).flatMap(
      (n) =>
        n.availableAccommodations.map((a) => [a.id, a.facilityTypes] as const),
    ),
  );

  return {
    nights,
    facilitiesByAccommodationId,
    stayTicketMasterId:
      data?.roomType?.availabilitiesByStayTicketMaster.stayTicketMasterId ??
      null,
    isRemote: data?.roomType?.site.isRemote ?? null,
    loading,
    error,
  };
}
